import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Scale, FileCheck, AlertTriangle, CheckCircle, Plus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function LawyerDashboard() {
  const { API } = useAuth();
  const [queue, setQueue] = useState([]);
  const [opinions, setOpinions] = useState([]);
  const [activeTab, setActiveTab] = useState("queue");
  const [showOpinion, setShowOpinion] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const [form, setForm] = useState({ opinion_type: "title_clear", summary: "", defects: "", recommendation: "" });

  const fetchData = useCallback(async () => {
    try {
      const [qRes, oRes] = await Promise.all([
        fetch(`${API}/lawyer/queue`, { credentials: "include" }),
        fetch(`${API}/lawyer/opinions`, { credentials: "include" }),
      ]);
      if (qRes.ok) setQueue(await qRes.json());
      if (oRes.ok) setOpinions(await oRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitOpinion = async () => {
    if (!selectedProp) return;
    try {
      const defectsArr = form.defects ? form.defects.split(",").map(d => d.trim()).filter(Boolean) : [];
      await fetch(`${API}/lawyer/opinion`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          property_id: selectedProp.property_id,
          opinion_type: form.opinion_type,
          summary: form.summary,
          defects: defectsArr,
          recommendation: form.recommendation,
        }),
      });
      setShowOpinion(false);
      setSelectedProp(null);
      setForm({ opinion_type: "title_clear", summary: "", defects: "", recommendation: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="lawyer-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Legal Intelligence</h2>
          <p className="text-white/40 text-sm font-sans">Title scrutiny, legal opinions, and defect analysis</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[{ key: "queue", label: "Scrutiny Queue" }, { key: "opinions", label: "My Opinions" }].map(t => (
          <button key={t.key} data-testid={`lawyer-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "queue" && (
        <>
          {queue.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Scale className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No properties pending title verification at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {queue.map(prop => (
                <div key={prop.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-[#D4AF37]" />
                    <h4 className="font-serif text-base text-white">{prop.name}</h4>
                  </div>
                  <p className="text-white/50 text-xs font-sans mb-1">{prop.address}</p>
                  {prop.survey_number && (
                    <p className="text-white/30 text-[10px] font-sans mb-3">Survey: {prop.survey_number}{prop.sub_division ? `/${prop.sub_division}` : ""}</p>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] px-2 py-0.5 rounded-sm font-sans">Title Under Verification</span>
                    <span className="text-white/30 text-[10px] font-sans capitalize">{prop.land_type || prop.property_type}</span>
                  </div>
                  <Button data-testid={`scrutinize-${prop.property_id}`} onClick={() => { setSelectedProp(prop); setShowOpinion(true); }}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans text-xs">
                    <FileCheck className="w-3 h-3 mr-1" /> Issue Opinion
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "opinions" && (
        <div className="space-y-3">
          {opinions.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <FileCheck className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No legal opinions issued yet.</p>
            </div>
          ) : opinions.map(o => (
            <div key={o.opinion_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Scale className="w-4 h-4 text-[#D4AF37]" />
                    <h4 className="font-sans font-medium text-white text-sm">{o.property?.name || o.property_id}</h4>
                  </div>
                  <p className="text-white/40 text-xs font-sans">{o.property?.address}</p>
                  <p className="text-white/60 text-xs font-sans mt-2">{o.summary}</p>
                  {o.defects?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {o.defects.map((d, i) => (
                        <span key={i} className="bg-red-400/10 text-red-400 text-[9px] px-1.5 py-0.5 rounded-sm font-sans flex items-center gap-0.5">
                          <AlertTriangle className="w-2.5 h-2.5" /> {d}
                        </span>
                      ))}
                    </div>
                  )}
                  {o.recommendation && <p className="text-white/50 text-xs font-sans mt-2 italic">{o.recommendation}</p>}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize ${o.opinion_type === "title_clear" ? "bg-[#556B2F]/10 text-[#556B2F]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}>
                  {o.opinion_type.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showOpinion} onOpenChange={setShowOpinion}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Issue Legal Opinion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedProp && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-white font-sans text-sm">{selectedProp.name}</p>
                <p className="text-white/40 text-xs font-sans">{selectedProp.address}</p>
              </div>
            )}
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Opinion Type</label>
              <Select value={form.opinion_type} onValueChange={v => setForm({ ...form, opinion_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="title_clear">Title Clear</SelectItem>
                  <SelectItem value="title_defective">Title Defective</SelectItem>
                  <SelectItem value="conditional_clear">Conditional Clear</SelectItem>
                  <SelectItem value="further_investigation">Further Investigation Needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Summary</label>
              <Input data-testid="opinion-summary" value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="Title chain analysis summary..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Defects (comma-separated)</label>
              <Input value={form.defects} onChange={e => setForm({ ...form, defects: e.target.value })} placeholder="e.g., Missing mutation entry, Encroachment noted" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Recommendation</label>
              <Input value={form.recommendation} onChange={e => setForm({ ...form, recommendation: e.target.value })} placeholder="Recommended next steps..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-opinion-btn" onClick={submitOpinion} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm">Issue Opinion</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
